/**
 * Claim a Sandboxes v2 sandbox. Shared by Test Environments and the Releases
 * timeline row menu so both claim through one mutation and give the same
 * feedback.
 *
 * @module hooks/useClaimSandboxV2
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useToast } from '@chatsdet/ui';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

/** Query key of the live sandbox list; claim and destroy both refresh it. */
export const SANDBOXES_V2_KEY = ['sandbox-v2-sandboxes'] as const;

export function useClaimSandboxV2() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { applicationId: string; environment: string; version?: string }) => {
      const res = await fetch(`${API_URL}/org/sandboxes-v2/sandboxes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to claim a sandbox');
      return ((await res.json()) as { data: { id: string } }).data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SANDBOXES_V2_KEY }),
  });
}

/** The toast feedback a claim gives (with an Open link to the sandbox), on Test Environments and Releases rows. */
export function useClaimFeedback(applicationName: string) {
  const { toast } = useToast();
  const navigate = useNavigate();
  return {
    onSuccess: (sandbox: { id: string }) =>
      toast.success(`Claiming a ${applicationName} sandbox`, {
        action: {
          label: 'Open',
          onClick: () => void navigate({ to: '/sandboxes/v2/$sandboxId', params: { sandboxId: sandbox.id } }),
        },
      }),
    onError: (error: Error) => toast.error(error.message),
  };
}
